import React, { useState, useEffect, useCallback } from 'react';
import { collection, query, getDocs, doc, getDoc, addDoc, deleteDoc, updateDoc, runTransaction, Timestamp } from 'firebase/firestore';
import { db } from '../App';
import { useFireAuth } from '../FireAuthContext';
import DelayRender from './DelayRender';
import useBank from './useBank';
import './Crafting.css';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaHammer, FaTrash, FaCheck } from 'react-icons/fa';

const debounce = (func, delay) => {
  let debounceTimer;
  return function() {
    const context = this;
    const args = arguments;
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => func.apply(context, args), delay);
  }
};

const Crafting = () => {
  const [items, setItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [craftedItemPreview, setCraftedItemPreview] = useState(null);
  const [craftingQueue, setCraftingQueue] = useState([]);
  const [queueItemsData, setQueueItemsData] = useState({});
  const [redeemingItems, setRedeemingItems] = useState({});
  const { user, updateUser } = useFireAuth();
  const { purchaseItem } = useBank();
  const navigate = useNavigate();

  const fetchItems = useCallback(async () => {
    const userInventory = user.inventory || [];
    const itemDocs = await Promise.all(userInventory.map(async (itemId) => {
      const originalItemId = itemId.split('_').slice(-1)[0];
      const itemDocRef = doc(db, 'items', originalItemId);
      const itemDocSnap = await getDoc(itemDocRef);
      if (itemDocSnap.exists()) {
        return {
          id: itemId,
          ...itemDocSnap.data()
        };
      } else {
        console.error(`Item with id ${originalItemId} does not exist`);
        return null;
      }
    }));
    setItems(itemDocs.filter(item => item !== null && item.craftable));
  }, [user]);

  const fetchCraftingQueue = useCallback(async () => {
    const craftingCollection = collection(db, 'users', user.uid, 'craftingQueue');
    const q = query(craftingCollection);
    const craftingSnapshot = await getDocs(q);
    const craftingList = craftingSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    setCraftingQueue(craftingList);
  }, [user]);

  useEffect(() => {
    fetchItems();
    fetchCraftingQueue();
  }, [fetchItems, fetchCraftingQueue]);

  useEffect(() => {
    const fetchQueueItemsData = async () => {
      const data = {};
      for (const item of craftingQueue) {
        if (item.craftedItems) {
          for (const craftedItem of item.craftedItems) {
            if (!craftedItem.itemId) {
              console.error(`Undefined crafted item ID in queue item ${item.id}`);
              continue;
            }
            const craftedItemDoc = doc(db, 'items', craftedItem.itemId);
            const craftedItemData = (await getDoc(craftedItemDoc)).data();
            if (!craftedItemData) {
              console.error(`Crafted item with ID ${craftedItem.itemId} does not exist`);
              continue;
            }
            data[craftedItem.itemId] = craftedItemData;
          }
        }
      }
      setQueueItemsData(data);
    };
    fetchQueueItemsData();
  }, [craftingQueue]);

  const handleSelectItem = (item) => {
    if (selectedItems.includes(item)) {
      setSelectedItems(selectedItems.filter(i => i.id !== item.id));
    } else {
      setSelectedItems([...selectedItems, item]);
    }
  };

  const getCraftedItemData = useCallback(async () => {
    const craftingRecipesCollection = collection(db, 'craftingRecipes');
    const q = query(craftingRecipesCollection);
    const querySnapshot = await getDocs(q);

    for (const doc of querySnapshot.docs) {
      const recipe = doc.data();
      const requiredItems = recipe.requiredItems;

      const selectedItemCounts = selectedItems.reduce((acc, item) => {
        const itemId = item.id.split('_').slice(-1)[0];
        acc[itemId] = (acc[itemId] || 0) + 1;
        return acc;
      }, {});

      const match = requiredItems.every((reqItem) => {
        return selectedItemCounts[reqItem.itemId] === reqItem.quantity;
      }) && Object.keys(selectedItemCounts).length === requiredItems.length;

      if (match) {
        return recipe;
      }
    }

    return null;
  }, [selectedItems]);

  useEffect(() => {
    const updateCraftedItemPreview = async () => {
      if (selectedItems.length > 0) {
        const recipe = await getCraftedItemData();
        if (recipe) {
          const craftedItemsData = await Promise.all(
            recipe.craftedItems.map(async (craftedItem) => {
              const craftedItemDoc = doc(db, 'items', craftedItem.itemId);
              const craftedItemData = (await getDoc(craftedItemDoc)).data();
              return { id: craftedItem.itemId, ...craftedItemData, quantity: craftedItem.quantity };
            })
          );
          setCraftedItemPreview({ craftedItems: craftedItemsData, craftTime: recipe.craftTime });
        } else {
          setCraftedItemPreview(null);
        }
      } else {
        setCraftedItemPreview(null);
      }
    };

    updateCraftedItemPreview();
  }, [selectedItems, getCraftedItemData]);

  const handleCraft = debounce(async () => {
    if (selectedItems.length === 0 || !craftedItemPreview) return;

    try {
      await runTransaction(db, async (transaction) => {
        const userDocRef = doc(db, 'users', user.uid);
        const userDocSnap = await transaction.get(userDocRef);
        const userInventory = userDocSnap.data().inventory || [];

        const allItemsExist = selectedItems.every(item => userInventory.includes(item.id));
        if (!allItemsExist) {
          alert('One or more selected items are no longer in your inventory.');
          window.location.reload();
          return;
        }

        const itemsToRemove = selectedItems.filter(item => !item.craftKeep);
        const newInventory = userInventory.filter(item => !itemsToRemove.map(i => i.id).includes(item));

        const craftingQueueCollection = collection(db, 'users', user.uid, 'craftingQueue');
        const craftedItemsData = craftedItemPreview.craftedItems.map(item => ({
          itemId: item.id,
          quantity: item.quantity
        }));
        if (craftedItemsData.some(item => !item.itemId)) {
          console.error('Attempted to add a crafted item without a valid itemId.');
          return;
        }

        const craftingDocRef = await addDoc(craftingQueueCollection, {
          selectedItems: selectedItems.map(item => item.id),
          craftedItems: craftedItemsData,
          craftTime: craftedItemPreview.craftTime,
          timestamp: Timestamp.fromDate(new Date()),
          status: 'in-progress'
        });

        transaction.update(userDocRef, { inventory: newInventory });

        console.log('Crafting started:', craftingDocRef.id);

        setCraftingQueue(prevQueue => [
          ...prevQueue,
          { id: craftingDocRef.id, selectedItems: selectedItems.map(item => item.id), craftedItems: craftedItemsData, craftTime: craftedItemPreview.craftTime, timestamp: Timestamp.fromDate(new Date()), status: 'in-progress' }
        ]);

        setSelectedItems([]);
        setCraftedItemPreview(null);
      });

      await updateUser();
    } catch (error) {
      console.error('Failed to craft item:', error);
    }
  }, 1000);

  const calculateRemainingTime = (timestamp, craftTime) => {
    const endTime = timestamp.toDate().getTime() + craftTime * 1000;
    const now = Date.now();
    const remainingTime = Math.max((endTime - now) / 1000, 0);
    return remainingTime;
  };

  const formatTime = (seconds) => {
    if (seconds >= 86400) {
      const d = Math.floor(seconds / 86400);
      const h = Math.floor((seconds % 86400) / 3600);
      return `${d}d ${h}h`;
    } else if (seconds >= 3600) {
      const h = Math.floor(seconds / 3600);
      const m = Math.floor((seconds % 3600) / 60);
      return `${h}h ${m}m`;
    } else if (seconds >= 60) {
      const m = Math.floor(seconds / 60);
      const s = Math.floor(seconds % 60);
      return `${m}m ${s}s`;
    } else {
      return `${seconds}s`;
    }
  };

  const handleRedeem = debounce(async (queueItemId, craftedItems) => {
    if (redeemingItems[queueItemId]) return;

    setRedeemingItems(prev => ({ ...prev, [queueItemId]: true }));

    try {
      const craftingDocRef = doc(db, 'users', user.uid, 'craftingQueue', queueItemId);
      await updateDoc(craftingDocRef, { status: 'redeemed' });

      for (const craftedItem of craftedItems) {
        const craftedItemDoc = doc(db, 'items', craftedItem.itemId);
        const craftedItemData = (await getDoc(craftedItemDoc)).data();
        for (let i = 0; i < craftedItem.quantity; i++) {
          await new Promise(resolve => setTimeout(resolve, 200));
          await purchaseItem({ ...craftedItemData, id: craftedItem.itemId, price: 0 });
        }
      }

      console.log('Item redeemed:', queueItemId);

      setCraftingQueue(prevQueue => prevQueue.filter(item => item.id !== queueItemId));
      await deleteDoc(craftingDocRef);
      await new Promise(resolve => setTimeout(resolve, 500));
      await updateUser();
    } catch (error) {
      console.error('Failed to redeem item:', error);
    } finally {
      setRedeemingItems(prev => ({ ...prev, [queueItemId]: false }));
    }
  }, 1000);

  const handleCancelCraft = debounce(async (queueItemId) => {
    try {
      const craftingDocRef = doc(db, 'users', user.uid, 'craftingQueue', queueItemId);
      const craftingData = (await getDoc(craftingDocRef)).data();

      for (const itemId of craftingData.selectedItems) {
        const itemDoc = doc(db, 'items', itemId.split('_').slice(-1)[0]);
        const itemData = (await getDoc(itemDoc)).data();
        await purchaseItem({ ...itemData, id: itemId, price: 0 });
      }

      console.log('Crafting canceled:', queueItemId);

      setCraftingQueue(prevQueue => prevQueue.filter(item => item.id !== queueItemId));

      await deleteDoc(craftingDocRef);
      await new Promise(resolve => setTimeout(resolve, 500));
      await updateUser();
    } catch (error) {
      console.error('Failed to cancel crafting item:', error);
    }
  }, 1000);

  useEffect(() => {
    const interval = setInterval(() => {
      setCraftingQueue(prevQueue => [...prevQueue]);
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="crafting-section">
      <FaArrowLeft className="back-arrow" onClick={() => navigate(-1)} />
      <div className="crafting-container">
        <div className="crafting-card inventory">
          <h3>Craftable Items</h3>
          <div className="items-grid">
            {items.map((item, index) => (
              <div
                key={`${item.id}-${index}`}
                className={`item-card ${selectedItems.includes(item) ? 'selected' : ''}`}
                onClick={() => handleSelectItem(item)}
              >
                <img src={item.imageUrl} alt={item.name} className="item-image" />
                <h3>{item.name}</h3>
                <p className="item-value">{item.currency === 'money' ? `💴 ${item.price}` : `🪙 ${item.price}`}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="crafting-row">
          <div className="crafting-card selected-items">
            <div className="crafting-selected-items-list">
              {selectedItems.map((item, index) => (
                <div key={`${item.id}-${index}`} className="selected-item">
                  <img src={item.imageUrl} alt={item.name} />
                  <p>{item.name}</p>
                </div>
              ))}
            </div>
            {craftedItemPreview && (
              <div className="crafted-item-preview">
                <h4>Preview:</h4>
                {craftedItemPreview.craftedItems && craftedItemPreview.craftedItems.map((craftedItem, index) => (
                  <div key={`${craftedItem.id}-${index}`} className="crafted-item">
                    <img src={craftedItem.imageUrl} alt={craftedItem.name} />
                    <div>
                      <p>{craftedItem.name}</p>
                      <div className="crafted-item-price">
                        <p>Value: {craftedItem.currency === 'money' ? `💴 ${craftedItem.price}` : `🪙 ${craftedItem.price}`}</p>
                      </div>
                      {craftedItem.quantity > 1 && <p>Quantity: {craftedItem.quantity}</p>}
                    </div>
                  </div>
                ))}
                <p>Crafting Time: {formatTime(craftedItemPreview.craftTime)}</p>
              </div>
            )}
            <div className="craft-button-container">
              <button onClick={handleCraft} disabled={selectedItems.length === 0 || !craftedItemPreview || craftingQueue.length >= 3} className="craft-button">
                <FaHammer />
              </button>
            </div>
          </div>
        </div>
        <div className="crafting-row">
          <div className="crafting-card crafting-queue">
          <div className="queue-header">
              <h3>Crafting Queue</h3>
              <span className="queue-limit">({craftingQueue.length}/3)</span>
            </div>
            <div className="queue-list">
              {craftingQueue.map((item, index) => {
                const remainingTime = calculateRemainingTime(item.timestamp, item.craftTime);
                const isReady = remainingTime === 0;
                const craftedItemDataList = item.craftedItems.map(craftedItem => queueItemsData[craftedItem.itemId]);

                return (
                  <div key={`${item.id}-${index}`} className="queue-item">
                    {craftedItemDataList.every(data => data) ? (
                      <>
                        <button onClick={() => handleCancelCraft(item.id)} className="cancel-button">
                          <FaTrash />
                        </button>
                        <div className="queue-item-details">
                          {craftedItemDataList.map((craftedItemData, idx) => (
                            <div key={`${craftedItemData.id}-${idx}`} className="crafted-item-box">
                              <img src={craftedItemData.imageUrl} alt={craftedItemData.name} className="queue-item-image" />
                              <p className="queue-item-name">{craftedItemData.name} {item.craftedItems[idx].quantity > 1 && `(x${item.craftedItems[idx].quantity})`}</p>
                            </div>
                          ))}
                        </div>
                        <div className="queue-item-status">
                          <p>Status: {isReady ? 'Ready' : 'In Progress'}</p>
                          <p>Time Remaining: {isReady ? '0s' : formatTime(remainingTime)}</p>
                        </div>
                        {isReady && (
                          <button 
                            onClick={() => handleRedeem(item.id, item.craftedItems)} 
                            className="redeem-button"
                            disabled={redeemingItems[item.id]}
                          >
                            <FaCheck />
                          </button>
                        )}
                      </>
                    ) : (
                      <p>Loading...</p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DelayRender(Crafting, 300);
